import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Footer from "../components/footer/footer";
import { api } from "../service/api.service";

const GUJARATI_FONT = "Noto Sans Gujarati, Philosopher, Arial, sans-serif";

export default function BookReader() {
  const { bookId } = useParams();
  const [loading, setLoading] = useState(true);

  // Collapse single line-breaks into flowing text, keep real paragraph breaks
  const normalizeContent = (html) => {
    if (!html) return html;
    let out = html.replace(/\t+/g, "").replace(/[ ]+/g, " ").trim();
    const PARA_BREAK = "PARA_BREAK_MARKER";
    // 2+ consecutive <br> => paragraph break marker
    out = out.replace(/(\s*<br\s*\/?>\s*){2,}/gi, PARA_BREAK);
    // remaining single <br> => join into the same flowing line
    out = out.replace(/<br\s*\/?>/gi, " ");
    out = out.split(PARA_BREAK).join("<br/><br/>");
    return out;
  };
  const [indexList, setIndexList] = useState([]);
  const [subIndexList, setSubIndexList] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [activeSubIndex, setActiveSubIndex] = useState(null);
  const [content, setContent] = useState(null);
  const [contentTitle, setContentTitle] = useState("Book Content");
  const [bookTitle, setBookTitle] = useState("Book");

  useEffect(() => {
    let ignore = false;

    const loadRootIndex = async () => {
      try {
        setLoading(true);
        setIndexList([]);
        setSubIndexList([]);
        setActiveIndex(null);
        setActiveSubIndex(null);
        setContent(null);

        const response = await fetch(`${api.defaults.baseURL}books/index/${bookId}`, {
          headers: { Accept: "application/json" },
        });
        const json = await response.json();
        const list = json?.data || [];

        if (!ignore) {
          setIndexList(list);
          if (list.length > 0) {
            setBookTitle(list[0]?.bookTitle || "Book");
            await handleSelectIndex(list[0]);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    if (bookId) {
      loadRootIndex();
    }

    return () => {
      ignore = true;
    };
  }, [bookId]);

  const loadContent = async (indexId, title) => {
    try {
      const response = await fetch(`${api.defaults.baseURL}books/content/${indexId}`, {
        headers: { Accept: "application/json" },
      });
      const json = await response.json();
      const data = json?.data || json;

      setContent(data);
      setContentTitle(title || "Book Content");
    } catch (err) {
      console.error(err);
      setContent(null);
    }
  };

  const loadSubIndex = async (indexId, title) => {
    try {
      const response = await fetch(`${api.defaults.baseURL}books/index/${indexId}?isSubIndex=true`, {
        headers: { Accept: "application/json" },
      });
      const json = await response.json();
      const list = json?.data || [];

      setSubIndexList(list);
      setActiveIndex({ _id: indexId, title });
      setActiveSubIndex(list[0] || null);

      if (list.length > 0 && list[0].hasContent) {
        await loadContent(list[0]._id, list[0].title);
      } else {
        setContent(null);
      }
    } catch (err) {
      console.error(err);
      setSubIndexList([]);
      setContent(null);
    }
  };

  const handleSelectIndex = async (item) => {
    setActiveIndex(item);
    setActiveSubIndex(null);
    setContent(null);
    setContentTitle(item.title || "Book Content");

    if (item.hasContent) {
      await loadContent(item._id, item.title);
      setSubIndexList([]);
      return;
    }

    await loadSubIndex(item._id, item.title);
  };

  const handleSelectSubIndex = async (item) => {
    setActiveSubIndex(item);
    setContent(null);
    setContentTitle(item.title || "Book Content");

    if (item.hasContent) {
      await loadContent(item._id, item.title);
    } else {
      await loadSubIndex(item._id, item.title);
    }
  };

  return (
    <>
      <section className="py-24 bg-[#FFF5F3]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-6 text-center">
            <h1
              className="text-center text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 lg:mb-10"
                style={{ fontFamily: GUJARATI_FONT }}
            >
              {bookTitle}
            </h1>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] overflow-hidden rounded-[22px] border border-slate-200 bg-white shadow-sm min-h-[760px]">
            <aside className="bg-[#e9f0f8] border-b lg:border-b-0 lg:border-r border-slate-200">
              {/* <div className="bg-[#1e73d8] text-white px-4 py-3 text-center text-[18px] font-semibold tracking-wide">
                {activeIndex?.title || "प्रકરણ"}
              </div> */}

              <div className="p-0">
                {loading ? (
                  <div className="p-4 text-slate-600">Loading…</div>
                ) : (
                  indexList.map((item) => {
                    const isActive = activeIndex?._id === item._id;
                    const showSubItems = activeIndex?._id === item._id && subIndexList.length > 0;

                    return (
                      <div key={item._id}>
                        <button
                          type="button"
                          onClick={() => handleSelectIndex(item)}
                          className={[
                            "w-full text-left px-5 py-3 border-b border-slate-200 transition-all duration-200",
                            "text-slate-700 hover:bg-white",
                            isActive ? "bg-[#1e73d8] text-white" : "",
                          ].join(" ")}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-[17px] md:text-[18px] leading-relaxed" style={{ fontFamily: GUJARATI_FONT }}>
                              {item.title}
                            </span>
                            {!item.hasContent && <span className="text-xl">›</span>}
                          </div>
                        </button>

                        {showSubItems && (
                          <div className="bg-[#f7f9fb] px-3 py-2 border-b border-slate-200">
                            {subIndexList.map((subItem) => {
                              const isSubActive = activeSubIndex?._id === subItem._id;
                              return (
                                <button
                                  key={subItem._id}
                                  type="button"
                                  onClick={() => handleSelectSubIndex(subItem)}
                                  className={[
                                    "w-full text-left px-3 py-2 rounded-md mb-1 transition-all duration-200",
                                    isSubActive ? "bg-[#1e73d8] text-white" : "text-slate-700 hover:bg-white",
                                  ].join(" ")}
                                >
                                  <span className="text-[16px] leading-relaxed" style={{ fontFamily: GUJARATI_FONT }}>
                                    {subItem.title}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </aside>

            <main className="bg-white p-6 md:p-10">
              {content ? (
                <div className="max-w-4xl mx-auto text-slate-800">
                  <div className="text-center mb-8">
                    <h2
                      className="text-[36px] md:text-[52px] font-bold text-[#b31b1b] leading-tight"
                      style={{ fontFamily: GUJARATI_FONT }}
                    >
                      {contentTitle}
                    </h2>
                  </div>

                  {/* {content.top_content && (
                    <div
                      className="mb-8 text-[18px] md:text-[22px] leading-relaxed text-slate-700 [&_p]:mb-4 [&_p:last-child]:mb-0"
                      style={{ fontFamily: GUJARATI_FONT }}
                      dangerouslySetInnerHTML={{ __html: normalizeContent(content.top_content) }}
                    />
                  )} */}

                  {content.middle_content && (
                    <div
                      className="text-[18px] md:text-[22px] leading-relaxed [&_p]:mb-4 [&_p:last-child]:mb-0"
                      style={{ fontFamily: GUJARATI_FONT }}
                      dangerouslySetInnerHTML={{ __html: normalizeContent(content.middle_content) }}
                    />
                  )}

                  {content.bottom_content && (
                    <div
                      className="mt-8 text-[18px] md:text-[22px] leading-relaxed text-slate-700 [&_p]:mb-4 [&_p:last-child]:mb-0"
                      style={{ fontFamily: GUJARATI_FONT }}
                      dangerouslySetInnerHTML={{ __html: normalizeContent(content.bottom_content) }}
                    />
                  )}
                </div>
              ) : (
                <div className="flex h-full min-h-[420px] items-center justify-center text-slate-600 text-lg">
                  {!loading && "Select a section to read the content."}
                </div>
              )}
            </main>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}
